import { MetadataFormData } from "@saleor/components/Metadata/types";
import { MultiAutocompleteChoiceType } from "@saleor/components/MultiAutocompleteSelectField";
import {
  SingleAutocompleteChoiceType,
  SingleAutocompleteChoiceTypeSeller
} from "@saleor/components/SingleAutocompleteSelectField";
import { ProductVariant } from "@saleor/fragments/types/ProductVariant";
import { FormsetAtomicData } from "@saleor/hooks/useFormset";
import { maybe } from "@saleor/misc";
import {
  ProductDetails_product,
  ProductDetails_product_collections,
  ProductDetails_product_variants
} from "@saleor/products/types/ProductDetails";
import { SearchProductTypes_search_edges_node_productAttributes } from "@saleor/searches/types/SearchProductTypes";
import { StockInput } from "@saleor/types/globalTypes";
import { mapMetadataItemToInput } from "@saleor/utils/maps";
import { RawDraftContentState } from "draft-js";

import { ProductAttributeInput } from "../components/ProductAttributes";
import { ProductStockInput } from "../components/ProductStocks";
import { VariantAttributeInput } from "../components/ProductVariantAttributes";
import { ProductVariant_productVariant_stockOnMall } from "../types/ProductVariant";
import { ProductVariantCreateData_product } from "../types/ProductVariantCreateData";

export interface Collection {
  id: string;
  label: string;
}

interface Node {
  id: string;
  name: string;
}

interface NodeSeller {
  id: string;
  name: string;
  sellers: string[];
}

export interface ProductType {
  hasVariants: boolean;
  id: string;
  name: string;
  productAttributes: SearchProductTypes_search_edges_node_productAttributes[];
}

export function getAttributeInputFromProduct(
  product: ProductDetails_product
): ProductAttributeInput[] {
  return maybe(
    (): ProductAttributeInput[] =>
      product.attributes.map(attribute => ({
        data: {
          inputType: attribute.attribute.inputType,
          isRequired: attribute.attribute.valueRequired,
          values: attribute.attribute.values
        },
        id: attribute.attribute.id,
        label: attribute.attribute.name,
        value: attribute.values.map(value => value.slug)
      })),
    []
  );
}

export interface ProductAttributeValueChoices {
  id: string;
  values: MultiAutocompleteChoiceType[];
}
export function getSelectedAttributesFromProduct(
  product: ProductDetails_product
): ProductAttributeValueChoices[] {
  return maybe(
    () =>
      product.attributes.map(attribute => ({
        id: attribute.attribute.id,
        values: attribute.values.map(value => ({
          label: value.name,
          value: value.slug
        }))
      })),
    []
  );
}

export function getAttributeInputFromProductType(
  productType: ProductType
): ProductAttributeInput[] {
  return productType.productAttributes.map(attribute => ({
    data: {
      inputType: attribute.inputType,
      isRequired: attribute.valueRequired,
      values: attribute.values
    },
    id: attribute.id,
    label: attribute.name,
    value: []
  }));
}

export function getAttributeInputFromVariant(
  variant: ProductVariant
): VariantAttributeInput[] {
  return maybe(
    (): VariantAttributeInput[] =>
      variant.attributes.map(attribute => ({
        data: {
          values: attribute.attribute.values
        },
        id: attribute.attribute.id,
        label: attribute.attribute.name,
        value: maybe(() => attribute.values[0].slug, null)
      })),
    []
  );
}

export function getStockInputFromVariant(
  variant: ProductVariant
): ProductStockInput[] {
  return (
    variant?.stocks.map(stock => ({
      data: null,
      id: stock.warehouse.id,
      label: stock.warehouse.name,
      value: stock.quantity.toString()
    })) || []
  );
}

export function getVariantAttributeInputFromProduct(
  product: ProductVariantCreateData_product
): VariantAttributeInput[] {
  return product?.productType?.variantAttributes?.map(attribute => ({
    data: {
      values: attribute.values
    },
    id: attribute.id,
    label: attribute.name,
    value: ""
  }));
}

export function getStockInputFromProduct(
  product: ProductDetails_product
): ProductStockInput[] {
  return product?.variants[0]?.stocks.map(stock => ({
    data: null,
    id: stock.warehouse.id,
    label: stock.warehouse.name,
    value: stock.quantity.toString()
  }));
}

export function getCollectionInput(
  productCollections: ProductDetails_product_collections[]
): Collection[] {
  return maybe(
    () =>
      productCollections.map(collection => ({
        id: collection.id,
        label: collection.name
      })),
    []
  );
}

export function getChoices(nodes: Node[]): SingleAutocompleteChoiceType[] {
  return maybe(
    () =>
      nodes.map(node => ({
        label: node.name,
        value: node.id
      })),
    []
  );
}

export function getChoicesDetails(
  nodes: NodeSeller[]
): SingleAutocompleteChoiceTypeSeller[] {
  return maybe(
    () =>
      nodes.map(node => ({
        label: node.name,
        sellers: node.sellers,
        value: node.id
      })),
    []
  );
}

export interface ProductUpdatePageFormData extends MetadataFormData {
  availableForPurchase: string;
  basePrice: number;
  brandLabel: string;
  category: string | null;
  changeTaxCode: boolean;
  chargeTaxes: boolean;
  collections: string[];
  description: RawDraftContentState;
  height: string;
  isAvailable: boolean;
  isAvailableForPurchase: boolean;
  isPublished: boolean;
  isPublishedByTenant: boolean;
  length: string;
  name: string;
  slug: string;
  productStatus: string;
  publicationDate: string;
  salePrice: string;
  seoDescription: string;
  seoTitle: string;
  sku: string;
  stockOnMall: string;
  taxCode: string;
  trackInventory: boolean;
  visibleInListings: boolean;
  weight: string;
  width: string;
  isAvailabilityTime: boolean;
  availabilityTimeStart: string;
  availabilityTimeEnd: string;
  productType: any;
  isSimpleProduct: boolean;
  brand: string;
  nonCod: boolean;
  tags: MultiAutocompleteChoiceType[];
}

export interface ProductUpdatePageFormDataOpts {
  /**
   * Store admins edit mall-scoped visibility via productVisibility.
   * Other roles use product-level isPublished / isAvailable.
   */
  useMallVisibility?: boolean;
}

function getVisibilityFormValues(
  product: ProductDetails_product,
  useMallVisibility: boolean
) {
  if (useMallVisibility) {
    const visibility = product?.productVisibility;
    return {
      // Mall-scoped flags (isAvailable / isVisible), not product-level isAvailableForPurchase
      isAvailableForPurchase: visibility?.isAvailable ?? false,
      isPublished: visibility?.isVisible ?? false
    };
  }

  return {
    // Non-store-admin: "Available for purchase" maps to product.isAvailable.
    // productSetAvailabilityForPurchase also takes `isAvailable`, and this product
    // can have isAvailable=true while isAvailableForPurchase=false.
    isAvailableForPurchase: product?.isAvailable ?? false,
    isPublished: product?.isPublished ?? false
  };
}

export function getProductUpdatePageFormData(
  product: ProductDetails_product,
  stockOnMall: ProductVariant_productVariant_stockOnMall,
  variants: ProductDetails_product_variants[],
  opts: ProductUpdatePageFormDataOpts = {}
): ProductUpdatePageFormData {
  const { useMallVisibility = false } = opts;
  const { isAvailableForPurchase, isPublished } = getVisibilityFormValues(
    product,
    useMallVisibility
  );

  return {
    availabilityTimeEnd: product?.availabilityTimeEnd || "",
    availabilityTimeStart: product?.availabilityTimeStart || "",
    availableForPurchase: product?.availableForPurchase,
    basePrice: maybe(() => product.variants[0].price.amount, 0),
    brand: maybe(() => product?.brand.id, ""),
    brandLabel: product?.brandLabel || "",
    category: maybe(() => product.category.id, ""),
    changeTaxCode: !!product?.taxType.taxCode,
    chargeTaxes: maybe(() => product.chargeTaxes, false),
    collections: maybe(
      () => product.collections.map(collection => collection.id),
      []
    ),
    description: maybe(() => JSON.parse(product.descriptionJson)),
    height: product?.height?.value.toString() || "",
    isAvailabilityTime: product?.isAvailabilityTime || false,
    isAvailable: !!product?.isAvailable,
    isAvailableForPurchase,
    isPublished,
    isPublishedByTenant: product?.isPublishedByTenant ?? false,
    isSimpleProduct: maybe(() => product.isSimpleProduct, false),
    length: product?.length?.value.toString() || "",
    metadata: product?.metadata?.map(mapMetadataItemToInput),
    name: maybe(() => product.name, ""),
    nonCod: maybe(() => product.nonCod, false),
    privateMetadata: product?.privateMetadata?.map(mapMetadataItemToInput),
    productStatus: product?.productStatus || "",
    productType: product?.productType,
    publicationDate: maybe(() => product.publicationDate, ""),
    salePrice: maybe(
      () => product.variants[0].sellingPrice.amount.toString(),
      ""
    ),
    seoDescription: maybe(() => product.seoDescription, ""),
    seoTitle: maybe(() => product.seoTitle, ""),
    sku: maybe(
      () => (variants && variants[0] ? variants[0].sku : undefined),
      ""
    ),
    slug: product?.slug || "",
    stockOnMall: stockOnMall?.quantity ? `${stockOnMall?.quantity}` : "0",
    tags: maybe(
      () =>
        product.tags.edges.map(edge => ({
          label: edge.node.name,
          value: edge.node.id
        })),
      []
    ),
    taxCode: product?.taxType.taxCode,
    trackInventory: !!product?.variants[0]?.trackInventory,
    visibleInListings: !!product?.visibleInListings,
    weight: product?.weight?.value.toString() || "",
    width: product?.width?.value.toString() || ""
  };
}

export function mapFormsetStockToStockInput(
  stock: FormsetAtomicData<null, string>
): StockInput {
  return {
    quantity: parseInt(stock.value, 10) || 0,
    warehouse: stock.id
  };
}
